/**
 * /investigations/{id} — investigation detail ("case archive" layout).
 *
 * v2.9 restructure:
 *   1. Header: ▸ INVESTIGATION <id-prefix> (mono, amber, NO glow) + status chip
 *      + live pulse if running. Back-link ← past investigations.
 *   2. Three top-level RetroAccordion sections:
 *      - [▼ REPL] (default OPEN) — ReplStream of the persisted timeline.
 *      - [▶ SYSTEM SNAPSHOT] (collapsed) — SystemSnapshotCard. Hidden if null.
 *      - [▶ FOLLOW-UP] (collapsed unless a thread exists) — follow-up Q&A + form.
 *   3. Failure note (existing) — between sections.
 *   4. Footer telemetry strip — rounds · cost · tokens · latency (mono, dim).
 *   5. DetectiveFooter when displayStatus === 'running'.
 *
 * Two render paths are preserved:
 *   - Terminal (status='done'/'failed'): fetch snapshot, render directly.
 *   - Running (status='running'): catchup SSE stream until done.
 */

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import IosShareIcon from '@mui/icons-material/IosShare';
import SendIcon from '@mui/icons-material/Send';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import { ReflectedBadge } from '@/components/ReflectedBadge';
import { RelatedInvestigations } from '@/components/RelatedInvestigations';
import { RetroAccordion } from '@/components/RetroAccordion';
import { ReplStream, adaptToolCallsToTimeline } from '@/components/ReplStream';
import { ShareToClaudeDialog } from '@/components/ShareToClaudeDialog';
import { DeleteInvestigationDialog } from '@/components/DeleteInvestigationDialog';
import { InvestigationFeedback } from '@/components/InvestigationFeedback';
import type { ReplAnchor, ReplStatus } from '@/components/ReplStream';
import type { TimelineEntry } from '@/components/InvestigationTimeline';
import { SystemSnapshotCard } from '@/components/SystemSnapshotCard';
import { ApiError, investigate } from '@/helpers/ApiHelper';
import { useInvestigateStream } from '@/hooks/useInvestigateStream';
import { useStartFollowupInvestigation } from '@/hooks/useStartFollowupInvestigation';
import { useAuth } from '@/contexts/AuthContext';
import { isAdminEmail } from '@/auth/admin';
import type {
  InvestigationAnchor,
  InvestigationDetail,
  InvestigationStatus,
} from '@/types/investigation';
import { formatRelative } from '@/utils/formatRelative';

function asAnchor(raw: InvestigationDetail['anchor']): InvestigationAnchor | null {
  if (!raw || typeof raw !== 'object') return null;
  const a = raw as Record<string, unknown>;
  if (
    typeof a.from_ts !== 'string' ||
    typeof a.to_ts !== 'string' ||
    !Array.isArray(a.services) ||
    !Array.isArray(a.symptoms) ||
    typeof a.raw_prompt !== 'string'
  ) {
    return null;
  }
  return a as unknown as InvestigationAnchor;
}

function adaptAnchor(anchor: InvestigationAnchor | null): ReplAnchor | null {
  if (!anchor) return null;
  return {
    from_iso: anchor.from_ts,
    to_iso: anchor.to_ts,
    services: anchor.services ?? [],
  };
}

function adaptStatus(s: InvestigationStatus | 'running'): ReplStatus {
  if (s === 'running') return 'streaming';
  if (s === 'done') return 'done';
  if (s === 'failed') return 'error';
  return 'idle';
}

// ─── Live chip ────────────────────────────────────────────────────────────────

function LiveChip(): ReactElement {
  return (
    <Chip
      size="small"
      color="warning"
      variant="outlined"
      icon={
        <FiberManualRecordIcon
          sx={{
            fontSize: 12,
            animation: 'pulse 1.6s ease-in-out infinite',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.3 },
            },
          }}
        />
      }
      label="Live"
      aria-label="Investigation is streaming live"
    />
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function InvestigationDetailPage(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = isAdminEmail(user?.email);
  const [snapshot, setSnapshot] = useState<InvestigationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ status: number; message: string } | null>(null);
  const stream = useInvestigateStream();
  const followup = useStartFollowupInvestigation();
  const [followupPrompt, setFollowupPrompt] = useState('');
  // ShareToClaudeDialog open state — hook-position-locked at the top per
  // rules-of-hooks (must run before any conditional early return below).
  const [shareOpen, setShareOpen] = useState(false);
  // DeleteInvestigationDialog open state — also hook-position-locked.
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    let active = true;
    setLoading(true);
    setError(null);
    investigate
      .get(id)
      .then((resp) => {
        if (!active) return;
        setSnapshot(resp);
      })
      .catch((err: unknown) => {
        if (!active) return;
        if (err instanceof ApiError) {
          setError({ status: err.status, message: err.detail });
        } else {
          setError({
            status: 0,
            message: err instanceof Error ? err.message : String(err),
          });
        }
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id]);

  useEffect(() => {
    if (!id || !snapshot) return;
    if (snapshot.status !== 'running') return;
    stream.start({ mode: 'catchup', investigationId: id });
    return () => {
      stream.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, snapshot?.status]);

  useEffect(() => {
    if (!id) return;
    if (stream.state.status !== 'done' && stream.state.status !== 'failed') return;
    if (!snapshot || snapshot.status === stream.state.status) return;
    investigate
      .get(id)
      .then((resp) => setSnapshot(resp))
      .catch(() => {
        /* best-effort refetch */
      });
  }, [id, stream.state.status, snapshot]);

  // Dispatch buddy-context events when the investigation's persisted status
  // transitions. Drive this off `snapshot.status` ONLY — the catchup stream
  // also reports `state.status === 'running'` during event replay, which
  // would spuriously make the buddy walk while a completed investigation
  // is just being viewed/replayed.
  const prevDisplayStatusRef = useRef<string | null>(null);
  const currentDisplayStatus = snapshot?.status ?? null;

  useEffect(() => {
    if (!currentDisplayStatus) return;
    const prev = prevDisplayStatusRef.current;
    prevDisplayStatusRef.current = currentDisplayStatus;
    if (prev === currentDisplayStatus) return;
    if (
      currentDisplayStatus === 'running' ||
      currentDisplayStatus === 'done' ||
      currentDisplayStatus === 'failed'
    ) {
      const identifier = id ? id.slice(0, 8) : undefined;
      // `job` carries the full UUID + type so DetectiveFooter's polling
      // reconciler can recover the terminal state if the user navigates
      // away before the stream completes.
      const job = id
        ? ({ type: 'investigation', id } as const)
        : undefined;
      window.dispatchEvent(
        new CustomEvent('sherlock:buddy-context', {
          detail: { kind: 'job', status: currentDisplayStatus, identifier, job },
        }),
      );
    }
  }, [currentDisplayStatus, id]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Box
          role="status"
          aria-label="Loading investigation"
          sx={{ display: 'flex', justifyContent: 'center', py: 6 }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Stack spacing={2}>
          <Button
            component={RouterLink}
            to="/investigations"
            startIcon={<ArrowBackIcon />}
            sx={{ alignSelf: 'flex-start' }}
          >
            Back to list
          </Button>
          <Alert severity={error.status === 404 ? 'warning' : 'error'} role="alert">
            {error.message}
          </Alert>
        </Stack>
      </Container>
    );
  }

  if (!snapshot) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Alert severity="warning" role="alert">
          No data
        </Alert>
      </Container>
    );
  }

  // Compute display state
  const isStreaming = stream.state.status === 'running';
  const anchor =
    isStreaming && stream.state.anchor ? stream.state.anchor : asAnchor(snapshot.anchor);
  const timeline: TimelineEntry[] = isStreaming
    ? stream.state.timeline
    : snapshot.tool_calls.map((c, idx) => ({
        call_id: `r${c.round}-${idx}`,
        round: c.round,
        tool: c.tool,
        args: c.args,
        result_preview: c.result_preview,
        latency_ms: c.latency_ms,
        error: c.error,
      }));
  const narrative = isStreaming ? stream.state.narrative : snapshot.narrative;
  const systemSnapshot = isStreaming
    ? stream.state.systemSnapshot
    : snapshot.system_snapshot;
  const displayStatus: InvestigationStatus | 'running' = isStreaming
    ? 'running'
    : snapshot.status;
  const errorMessage = isStreaming ? null : snapshot.error_message;

  const rounds = isStreaming ? stream.state.rounds || timeline.length : snapshot.rounds;
  const costUsd = isStreaming ? stream.state.costUsd : snapshot.cost_usd;
  const tokensUsed = isStreaming ? stream.state.tokensUsed : snapshot.tokens_used;

  // Short ID for the header — first 8 chars
  const shortId = snapshot.id.slice(0, 8).toUpperCase();

  // Trigger is disabled until status === 'done'. `shareOpen` is declared
  // alongside the other top-of-component hooks above.
  const canShare = displayStatus === 'done';

  // Soft-delete state — used to show the admin banner + hide the Delete button.
  const isDeleted = Boolean(snapshot.deleted_at);

  // Follow-up accordion is open by default when a follow-up thread exists.
  // We detect this by checking if any follow-up prompt has been entered or the
  // snapshot's status is done (user can post follow-up when done).
  const followupDefaultOpen = displayStatus === 'done' && Boolean(followupPrompt);

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3 }, px: { xs: 1.5, sm: 3 } }}>
      <Stack spacing={{ xs: 1.5, sm: 2 }}>
        {/* v2.9 — Header */}
        <Box>
          {/* Back link */}
          <Box sx={{ mb: 1 }}>
            <Button
              component={RouterLink}
              to="/investigations"
              startIcon={<ArrowBackIcon />}
              size="small"
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.75rem',
                color: 'text.secondary',
              }}
            >
              ← past investigations
            </Button>
          </Box>

          {/* Main header row — stacks vertically on xs, horizontal on sm+ */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1}
            flexWrap="wrap"
          >
            {/* ID heading — mono, amber, NO glow per v2.2 */}
            <Typography
              component="h1"
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: 'primary.main',
                flexGrow: 1,
              }}
            >
              ▸ INVESTIGATION {shortId}
            </Typography>

            {/* Live pulse chip if running */}
            {isStreaming && <LiveChip />}

            {/* Status chip */}
            <Chip
              size="small"
              label={displayStatus}
              color={
                displayStatus === 'done'
                  ? 'success'
                  : displayStatus === 'failed'
                    ? 'error'
                    : 'warning'
              }
              variant="outlined"
              sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.6875rem' }}
            />

            {/* Confidence chip — only shown when backend provides the field */}
            {typeof snapshot.confidence === 'number' && (
              <Typography variant="caption" color="text.secondary">
                {Math.round(snapshot.confidence * 100)}% confidence
              </Typography>
            )}

            {/* Sprint 12 Phase C — reflection badge. Hidden when the critic
                hasn't run yet (or never will, e.g. legacy rows). */}
            <ReflectedBadge reflection={snapshot.reflection ?? null} />

            {/* Relative time */}
            <Typography
              variant="caption"
              // eslint-disable-next-line @typescript-eslint/no-explicit-any -- reason: text.faintest not in MUI default typing
              color={(theme) => (theme.palette as any).text?.faintest ?? theme.palette.text.disabled}
              sx={{ fontFamily: '"JetBrains Mono", monospace', flexShrink: 0 }}
            >
              {formatRelative(snapshot.created_at)}
            </Typography>

            {/* Share-to-Claude — disabled until status is 'done' */}
            <Button
              variant="text"
              disabled={!canShare}
              onClick={() => setShareOpen(true)}
              endIcon={<IosShareIcon fontSize="small" />}
              sx={{
                minHeight: 44,
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: 12,
                textTransform: 'none',
                color: 'text.secondary',
                '&:hover': { color: 'primary.main' },
              }}
            >
              share to claude ↗
            </Button>

            {/* Delete — rightmost, only shown when not already deleted */}
            {!isDeleted && (
              <Button
                variant="text"
                color="error"
                onClick={() => setDeleteOpen(true)}
                startIcon={<DeleteOutlineIcon fontSize="small" />}
                aria-label="Delete this investigation"
                sx={{
                  minHeight: 44,
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: 12,
                  textTransform: 'none',
                  ml: 1,
                }}
              >
                delete
              </Button>
            )}
          </Stack>
        </Box>

        {canShare && (
          <ShareToClaudeDialog
            open={shareOpen}
            onClose={() => setShareOpen(false)}
            target={{ type: 'investigation', investigationId: snapshot.id }}
          />
        )}

        {/* Admin banner — shown when an admin views a soft-deleted investigation */}
        {isAdmin && isDeleted && snapshot.deleted_at && (
          <Alert severity="warning" role="alert">
            This investigation was deleted on{' '}
            {new Date(snapshot.deleted_at).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
            . It is hidden from all other users.
          </Alert>
        )}

        <DeleteInvestigationDialog
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          investigation={{
            id: snapshot.id,
            prompt: snapshot.prompt,
            created_at: snapshot.created_at,
          }}
        />

        {/* Sprint 12 Phase B — related-investigations chip panel.
            Surfaced above REPL so users see prior episodes before the
            tool-call log starts streaming. Empty/null => renders nothing. */}
        {isStreaming && (
          <RelatedInvestigations episodes={stream.state.memoryEpisodes} />
        )}

        {/* v2.9 — Three RetroAccordion sections */}

        {/* Section 1: REPL (default open) */}
        <RetroAccordion title="REPL" defaultOpen={true}>
          <Box sx={{ pt: 1, pb: 1 }}>
            <ReplStream
              prompt={snapshot.prompt}
              anchor={adaptAnchor(anchor)}
              timeline={adaptToolCallsToTimeline(timeline)}
              narrative={narrative}
              status={adaptStatus(displayStatus)}
            />
          </Box>
        </RetroAccordion>

        {/* Feedback widget — sits below the REPL narrative + tool-call log
            (Sprint 12 Phase A). Hidden until the investigation is done so
            users don't rate an in-flight run. */}
        {displayStatus === 'done' && id && (
          <InvestigationFeedback sourceType="investigation" sourceId={id} />
        )}

        {/* Section 2: SYSTEM SNAPSHOT (collapsed; skip if null) */}
        {systemSnapshot !== null && (
          <RetroAccordion title="SYSTEM SNAPSHOT" defaultOpen={false}>
            <Box sx={{ pt: 1, pb: 1 }}>
              <SystemSnapshotCard snapshot={systemSnapshot} />
            </Box>
          </RetroAccordion>
        )}

        {/* Failure note sits outside accordions so it's always visible */}
        {displayStatus === 'failed' && errorMessage && (
          <Alert severity="error" role="alert">
            {errorMessage}
          </Alert>
        )}

        {/* Section 3: FOLLOW-UP (collapsed by default; open if thread exists) */}
        {displayStatus === 'done' && id && (
          <RetroAccordion
            title="FOLLOW-UP"
            defaultOpen={followupDefaultOpen}
          >
            <Box
              sx={{
                pt: 2,
                pb: 2,
                px: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Refine the answer with a new question. The agent reuses this
                investigation&apos;s tool calls + narrative, so follow-ups are
                cheaper and avoid redundant work.
              </Typography>
              <Stack
                component="form"
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                alignItems="flex-start"
                onSubmit={async (e: React.FormEvent) => {
                  e.preventDefault();
                  const trimmed = followupPrompt.trim();
                  if (!trimmed || followup.state.status === 'submitting') return;
                  try {
                    const newId = await followup.submit(trimmed, id);
                    setFollowupPrompt('');
                    navigate(`/investigations/${newId}`);
                  } catch {
                    /* hook surfaces error in state */
                  }
                }}
              >
                <TextField
                  id="investigate-followup-prompt"
                  label="Follow-up question"
                  placeholder="e.g. did the deploy revert succeed? (shift+enter to submit)"
                  value={followupPrompt}
                  onChange={(e) => setFollowupPrompt(e.target.value)}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter' && e.shiftKey) {
                      e.preventDefault();
                      const trimmed = followupPrompt.trim();
                      if (!trimmed || followup.state.status === 'submitting') return;
                      try {
                        const newId = await followup.submit(trimmed, id);
                        setFollowupPrompt('');
                        navigate(`/investigations/${newId}`);
                      } catch {
                        /* hook surfaces error in state */
                      }
                    }
                  }}
                  multiline
                  minRows={1}
                  maxRows={4}
                  fullWidth
                  size="small"
                  disabled={followup.state.status === 'submitting'}
                  inputProps={{ maxLength: 4000 }}
                />
                <Button
                  type="submit"
                  aria-label="Submit follow-up question"
                  color="primary"
                  variant="contained"
                  size="large"
                  disabled={
                    followupPrompt.trim().length === 0 ||
                    followup.state.status === 'submitting'
                  }
                  startIcon={
                    followup.state.status === 'submitting' ? (
                      <CircularProgress size={20} aria-hidden="true" />
                    ) : (
                      <SendIcon />
                    )
                  }
                  sx={{
                    alignSelf: { xs: 'stretch', sm: 'flex-start' },
                    mt: { xs: 0, sm: 0.25 },
                    minHeight: 44,
                    // On sm+: shrink to icon-like (square-ish) button
                    minWidth: { xs: 'unset', sm: 44 },
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                    Submit
                  </Box>
                </Button>
              </Stack>
              {followup.state.status === 'error' && followup.state.error && (
                <Alert severity="error" role="alert" sx={{ mt: 2 }}>
                  {followup.state.error.message}
                </Alert>
              )}
            </Box>
          </RetroAccordion>
        )}

        {/* v2.9 — Footer telemetry strip (mono, dim) */}
        <Box
          sx={{
            pt: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack
            direction="row"
            spacing={{ xs: 1.5, sm: 3 }}
            flexWrap="wrap"
            useFlexGap
            sx={{ rowGap: 0.5 }}
          >
            {[
              `rounds: ${rounds}`,
              `cost: $${costUsd.toFixed(4)}`,
              `tokens: ${tokensUsed.toLocaleString()}`,
              `latency: ${snapshot.latency_ms.toLocaleString()}ms`,
            ].map((label) => (
              <Typography
                key={label}
                variant="caption"
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- reason: text.faintest not in MUI default typing
                color={(theme) => (theme.palette as any).text?.faintest ?? theme.palette.text.disabled}
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {label}
              </Typography>
            ))}
          </Stack>
        </Box>
      </Stack>

    </Container>
  );
}
